import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Video, WorkoutSession, ScheduledWorkout, ProgressStats, Difficulty, MuscleGroup } from '../types';

interface PilatesState {
  videos: Video[];
  sessions: WorkoutSession[];
  scheduledWorkouts: ScheduledWorkout[];
  progress: ProgressStats;
  selectedDifficulty: Difficulty | 'all';
  selectedMuscleGroup: MuscleGroup | 'all';
  
  // Actions
  setSelectedDifficulty: (difficulty: Difficulty | 'all') => void;
  setSelectedMuscleGroup: (muscleGroup: MuscleGroup | 'all') => void;
  addSession: (session: WorkoutSession) => void;
  scheduleWorkout: (workout: ScheduledWorkout) => void;
  completeWorkout: (id: string) => void;
  deleteScheduledWorkout: (id: string) => void;
  updateProgress: () => void;
}

// Sample video library with YouTube references
const sampleVideos: Video[] = [
  {
    id: '1',
    title: 'Day 1: Core & Sliding Fundamentals',
    description: 'Day 1 of the Alligné Studio challenge! A complete core workout featuring elbow sliding, hand sliding, hip openers, crunches, and glute bridges using the Pilates board.',
    duration: 23,
    difficulty: 'beginner',
    thumbnail: 'https://i.ytimg.com/vi/KH-WbVeUSxk/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=KH-WbVeUSxk&t=378s',
    category: 'Morning',
    muscleGroup: 'core',
    instructor: 'Alligné Studio',
    tags: ['core', 'sliding', 'beginner', 'day1', 'morning']
  },
  {
    id: '2',
    title: 'Day 2: Core & Lower Body',
    description: 'Day 2 challenge! Focus on core, glutes, and lower body with resistance bands.',
    duration: 20,
    difficulty: 'intermediate',
    thumbnail: 'https://i.ytimg.com/vi/rX0z4rEuZYA/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=rX0z4rEuZYA&t=3s',
    category: 'Toning',
    muscleGroup: 'lower-body',
    instructor: 'Alligné Studio',
    tags: ['core', 'lower body', 'glutes', 'day2', 'toning']
  },
  {
    id: '3',
    title: 'Day 3: Full Body Flow',
    description: 'Day 3 complete workout! Full body flow with glute bridges, lunges, push-ups, and more.',
    duration: 26,
    difficulty: 'intermediate',
    thumbnail: 'https://i.ytimg.com/vi/32jDTNTTWV0/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=32jDTNTTWV0&t=24s',
    category: 'Cardio',
    muscleGroup: 'full-body',
    instructor: 'Alligné Studio',
    tags: ['full body', 'flow', 'day3', 'cardio']
  },
  {
    id: '4',
    title: 'Day 4: Upper Body & Arms',
    description: 'Day 4 focuses on arms and upper body with resistance bands.',
    duration: 20,
    difficulty: 'intermediate',
    thumbnail: 'https://i.ytimg.com/vi/_q7L4MNgblc/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=_q7L4MNgblc',
    category: 'Toning',
    muscleGroup: 'upper-body',
    instructor: 'Alligné Studio',
    tags: ['arms', 'upper body', 'bands', 'day4', 'toning']
  },
  {
    id: '5',
    title: 'Day 5: Core & Standing',
    description: 'Day 5! Core and standing exercises with the Pilates board.',
    duration: 30,
    difficulty: 'intermediate',
    thumbnail: 'https://i.ytimg.com/vi/RWFJFob3apA/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=RWFJFob3apA',
    category: 'Balance',
    muscleGroup: 'core',
    instructor: 'Alligné Studio',
    tags: ['core', 'standing', 'squats', 'day5', 'balance']
  },
  {
    id: '6',
    title: 'Day 6: Full Body Burn',
    description: 'Day 6 full body workout!',
    duration: 30,
    difficulty: 'advanced',
    thumbnail: 'https://i.ytimg.com/vi/1DeOnrevcaQ/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=1DeOnrevcaQ',
    category: 'Cardio',
    muscleGroup: 'full-body',
    instructor: 'Alligné Studio',
    tags: ['full body', 'advanced', 'burn', 'day6', 'cardio']
  },
  {
    id: '7',
    title: 'Day 7: Core & Glutes',
    description: 'Day 7! Core and glute focused workout.',
    duration: 30,
    difficulty: 'intermediate',
    thumbnail: 'https://i.ytimg.com/vi/OMxFhHp6Moo/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=OMxFhHp6Moo',
    category: 'Back Health',
    muscleGroup: 'glutes',
    instructor: 'Alligné Studio',
    tags: ['core', 'glutes', 'day7', 'back health']
  },
  {
    id: '8',
    title: 'Day 8: Full Body Band Workout',
    description: 'Day 8 with resistance bands!',
    duration: 30,
    difficulty: 'advanced',
    thumbnail: 'https://i.ytimg.com/vi/oPm_QXBpIDU/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=oPm_QXBpIDU',
    category: 'Toning',
    muscleGroup: 'full-body',
    instructor: 'Alligné Studio',
    tags: ['bands', 'full body', 'advanced', 'day8', 'toning']
  },
  {
    id: '9',
    title: 'Day 9: Lower Body Focus',
    description: 'Day 9 lower body focused!',
    duration: 31,
    difficulty: 'advanced',
    thumbnail: 'https://i.ytimg.com/vi/yEREs2yPrFA/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=yEREs2yPrFA',
    category: 'Lower Body',
    muscleGroup: 'lower-body',
    instructor: 'Alligné Studio',
    tags: ['lower body', 'advanced', 'squats', 'day9', 'strength']
  },
  {
    id: '10',
    title: 'Day 10: Lower Body & Core',
    description: 'Day 10! Lower body and core.',
    duration: 21,
    difficulty: 'intermediate',
    thumbnail: 'https://i.ytimg.com/vi/ypLmheTbGLQ/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=ypLmheTbGLQ',
    category: 'Flexibility',
    muscleGroup: 'lower-body',
    instructor: 'Alligné Studio',
    tags: ['lower body', 'core', 'day10', 'flexibility']
  },
  {
    id: '11',
    title: 'Day 11: Arms & Abs',
    description: 'Day 11 arms and abs focused!',
    duration: 33,
    difficulty: 'intermediate',
    thumbnail: 'https://i.ytimg.com/vi/obvn3dyK5qU/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=obvn3dyK5qU',
    category: 'Toning',
    muscleGroup: 'upper-body',
    instructor: 'Alligné Studio',
    tags: ['arms', 'abs', 'day11', 'toning']
  },
  {
    id: '12',
    title: 'Day 12: Glutes & Abs Finale',
    description: 'Day 12 finale! Glutes and abs.',
    duration: 33,
    difficulty: 'advanced',
    thumbnail: 'https://i.ytimg.com/vi/vT7cFOddhSE/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=vT7cFOddhSE',
    category: 'Recovery',
    muscleGroup: 'glutes',
    instructor: 'Alligné Studio',
    tags: ['glutes', 'abs', 'advanced', 'finale', 'day12', 'recovery']
  },
  {
    id: '13',
    title: 'Day 13: Complete Challenge',
    description: 'Day 13 complete challenge!',
    duration: 35,
    difficulty: 'advanced',
    thumbnail: 'https://i.ytimg.com/vi/9USiylZl990/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=9USiylZl990',
    category: 'Advanced',
    muscleGroup: 'full-body',
    instructor: 'Alligné Studio',
    tags: ['full body', 'challenge', 'complete', 'day13', 'advanced']
  },
  {
    id: '14',
    title: 'Day 14: Advanced Flow',
    description: 'Day 14 advanced Pilates flow with sliding exercises and resistance training.',
    duration: 30,
    difficulty: 'advanced',
    thumbnail: 'https://i.ytimg.com/vi/PnCwjQzW8g0/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=PnCwjQzW8g0',
    category: 'Advanced',
    muscleGroup: 'full-body',
    instructor: 'Alligné Studio',
    tags: ['full body', 'advanced', 'sliding', 'day14', 'advanced']
  },
  {
    id: '15',
    title: 'Day 15: Master Challenge',
    description: 'Day 15 master challenge - the ultimate Pilates board workout!',
    duration: 30,
    difficulty: 'advanced',
    thumbnail: 'https://img.youtube.com/vi/BLNrn96Jp0M/hqdefault.jpg',
    videoUrl: 'https://www.youtube.com/watch?v=BLNrn96Jp0M',
    category: 'Advanced',
    muscleGroup: 'full-body',
    instructor: 'Alligné Studio',
    tags: ['full body', 'master', 'challenge', 'day15', 'advanced']
  }
];

const initialProgress: ProgressStats = {
  totalWorkouts: 0,
  totalMinutes: 0,
  currentStreak: 0,
  longestStreak: 0,
  weeklyGoal: 5,
  weeklyProgress: 0
};

export const usePilatesStore = create<PilatesState>()(
  persist(
    (set, get) => ({
      videos: sampleVideos,
      sessions: [],
      scheduledWorkouts: [],
      progress: initialProgress,
      selectedDifficulty: 'all',
      selectedMuscleGroup: 'all',

      setSelectedDifficulty: (difficulty) => set({ selectedDifficulty: difficulty }),
      setSelectedMuscleGroup: (muscleGroup) => set({ selectedMuscleGroup: muscleGroup }),

      addSession: (session) => set((state) => {
        const newSessions = [...state.sessions, session];
        return { sessions: newSessions };
      }),

      scheduleWorkout: (workout) => set((state) => ({
        scheduledWorkouts: [...state.scheduledWorkouts, workout]
      })),

      completeWorkout: (id) => set((state) => {
        const updatedWorkouts = state.scheduledWorkouts.map(w =>
          w.id === id ? { ...w, completed: true } : w
        );
        return { scheduledWorkouts: updatedWorkouts };
      }),

      deleteScheduledWorkout: (id) => set((state) => ({
        scheduledWorkouts: state.scheduledWorkouts.filter(w => w.id !== id)
      })),

      // Complete a scheduled workout and log it as a session
      completeWorkoutAndLog: (id: string) => set((state) => {
        const workout = state.scheduledWorkouts.find(w => w.id === id);
        if (!workout) return state;
        
        const video = state.videos.find(v => v.id === workout.videoId);
        
        // Create a new session
        const newSession: WorkoutSession = {
          id: Date.now().toString(),
          videoId: workout.videoId,
          date: new Date().toISOString(),
          duration: video?.duration || 0,
          completed: true,
          notes: '',
          rating: undefined
        };
        
        // Update scheduled workout and add session
        const updatedWorkouts = state.scheduledWorkouts.map(w =>
          w.id === id ? { ...w, completed: true } : w
        );
        
        return {
          sessions: [...state.sessions, newSession],
          scheduledWorkouts: updatedWorkouts
        };
      }),

      // Log a workout session directly (from video modal)
      logWorkout: (videoId: string, duration: number, notes?: string, rating?: number) => set((state) => {
        const video = state.videos.find(v => v.id === videoId);
        
        const newSession: WorkoutSession = {
          id: Date.now().toString(),
          videoId,
          date: new Date().toISOString(),
          duration: duration || video?.duration || 0,
          completed: true,
          notes,
          rating
        };
        
        return {
          sessions: [...state.sessions, newSession]
        };
      }),

      // Update session rating
      rateSession: (sessionId: string, rating: number) => set((state) => ({
        sessions: state.sessions.map(s =>
          s.id === sessionId ? { ...s, rating } : s
        )
      })),

      // Delete a session
      deleteSession: (sessionId: string) => set((state) => ({
        sessions: state.sessions.filter(s => s.id !== sessionId)
      })),

      // Update weekly goal
      setWeeklyGoal: (goal: number) => set((state) => ({
        progress: { ...state.progress, weeklyGoal: goal }
      })),

      updateProgress: () => set((state) => {
        const sessions = state.sessions;
        const today = new Date();
        
        // Calculate total workouts and minutes
        const totalWorkouts = sessions.length;
        const totalMinutes = sessions.reduce((acc, s) => acc + s.duration, 0);

        // Calculate streak
        let currentStreak = 0;
        let longestStreak = 0;
        let tempStreak = 0;
        
        const sortedDates = [...new Set(sessions.map(s => s.date.split('T')[0]))].sort().reverse();
        
        for (let i = 0; i < sortedDates.length; i++) {
          const checkDate = new Date(today);
          checkDate.setDate(checkDate.getDate() - i);
          const checkDateStr = checkDate.toISOString().split('T')[0];
          
          if (sortedDates.includes(checkDateStr)) {
            tempStreak++;
            currentStreak = tempStreak;
          } else {
            break;
          }
        }

        // Calculate longest streak
        tempStreak = 0;
        for (let i = 0; i < sortedDates.length - 1; i++) {
          const current = new Date(sortedDates[i]);
          const prev = new Date(sortedDates[i + 1]);
          const diff = Math.abs(current.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24);
          
          if (diff === 1) {
            tempStreak++;
          } else {
            longestStreak = Math.max(longestStreak, tempStreak);
            tempStreak = 0;
          }
        }
        longestStreak = Math.max(longestStreak, tempStreak + 1);

        // Calculate weekly progress
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weeklySessions = sessions.filter(s => new Date(s.date) >= weekStart);
        const weeklyProgress = weeklySessions.length;

        return {
          progress: {
            ...state.progress,
            totalWorkouts,
            totalMinutes,
            currentStreak,
            longestStreak,
            weeklyProgress
          }
        };
      })
    }),
    {
      name: 'pilates-storage',
      version: 2
    }
  )
);
